/**
 * Video Link Overlay Module
 * 
 * Creates clickable link overlays above video elements.
 * Links are derived from sibling Kadence buttons (source of truth).
 * Provides visual feedback on hover for better UX.
 */

/**
 * Remove trailing -/ patterns from URL
 */
function cleanupUrl(url) {
    return url.replace(/(-\/)+$/, '');
}

/**
 * Find video blocks within a container
 * Searches for both figure.wp-block-video and direct video elements
 */
function findVideoBlocks(container) {
    const videoBlocks = [];
    
    // Find figure.wp-block-video elements
    const figures = container.querySelectorAll('figure.wp-block-video');
    figures.forEach(function(figure) {
        videoBlocks.push(figure);
    });
    
    // Find direct video elements not inside figures
    const videos = container.querySelectorAll('video');
    videos.forEach(function(video) {
        const parentFigure = video.closest('figure.wp-block-video');
        if (!parentFigure) {
            videoBlocks.push(video);
        }
    });
    
    return videoBlocks;
}

/**
 * Get default video overlay label (translated)
 */
function getDefaultVideoLabel() {
    return (window.novigranFseL10n && window.novigranFseL10n.videoViewMore) || 'Ver más';
}

/**
 * Create the link overlay element
 */
function createLinkOverlay(href, buttonText) {
    const defaultLabel = getDefaultVideoLabel();
    const overlay = document.createElement('a');
    overlay.setAttribute('href', href);
    overlay.setAttribute('class', 'video-link-overlay');
    overlay.setAttribute('aria-label', buttonText || defaultLabel);
    overlay.setAttribute('draggable', 'false');
    
    // Create hover label
    const label = document.createElement('span');
    label.setAttribute('class', 'video-link-overlay__label');
    label.textContent = buttonText || defaultLabel;
    overlay.appendChild(label);
    
    return overlay;
}

/**
 * Add link overlay to a video block
 * Wraps the video in a container and places the overlay as a sibling
 */
function addVideoLinkOverlay(videoBlock, sourceUrl, buttonText) {
    const parent = videoBlock.parentElement;
    
    if (!parent) {
        return false;
    }
    
    // Check if wrapper already exists
    const existingWrapper = videoBlock.closest('.video-link-wrapper');
    
    if (existingWrapper) {
        const existingOverlay = existingWrapper.querySelector('.video-link-overlay');
        
        if (existingOverlay) {
            const currentHref = existingOverlay.getAttribute('href');
            const cleanedCurrentHref = cleanupUrl(currentHref);
            
            // Update if URL changed
            if (cleanedCurrentHref !== sourceUrl || currentHref !== cleanedCurrentHref) {
                existingOverlay.setAttribute('href', sourceUrl);
                return true;
            }
            
            return false;
        }
    }
    
    // Create wrapper container
    const wrapper = document.createElement('div');
    wrapper.setAttribute('class', 'video-link-wrapper');
    
    // Insert wrapper before video block
    parent.insertBefore(wrapper, videoBlock);
    
    // Move video block inside wrapper
    wrapper.appendChild(videoBlock);
    
    // Create and insert overlay before video block (inside wrapper)
    const overlay = createLinkOverlay(sourceUrl, buttonText);
    wrapper.insertBefore(overlay, videoBlock);
    
    return true;
}

/**
 * Find the common ancestor container for button and videos
 * Looks for wp-block-columns or wp-block-group containers
 */
function findCommonContainer(kadenceButton) {
    let current = kadenceButton.parentElement;
    
    while (current) {
        // Check for column layouts that typically contain video + button pairs
        if (current.matches('.wp-block-columns, .wp-block-group[class*="alignfull"]')) {
            return current;
        }
        current = current.parentElement;
    }
    
    return null;
}

/**
 * Process a Kadence button and add overlays to related videos
 */
function processKadenceButtonForVideos(kadenceButton) {
    const anchor = kadenceButton.querySelector('a[href]');
    
    if (!anchor) {
        return;
    }
    
    const href = anchor.getAttribute('href');
    
    if (!href || href === '#' || href === '') {
        return;
    }
    
    const sourceUrl = cleanupUrl(href);
    const buttonText = anchor.textContent.trim();
    
    // Find common container
    const container = findCommonContainer(kadenceButton);
    
    if (!container) {
        return;
    }
    
    // Find video blocks in the container
    const videoBlocks = findVideoBlocks(container);
    
    videoBlocks.forEach(function(videoBlock) {
        addVideoLinkOverlay(videoBlock, sourceUrl, buttonText);
    });
}

/**
 * Initialize Video Link Overlay
 */
export default function initVideoLinkOverlay() {
    const kadenceButtons = document.querySelectorAll('.wp-block-kadence-advancedbtn');
    
    if (!kadenceButtons.length) {
        return;
    }
    
    kadenceButtons.forEach(function(button) {
        processKadenceButtonForVideos(button);
    });
}

